import {
  getCanonicalLocales,
  invariant,
  getOption,
  resolveLocale,
  decode256
} from './utils.js'
import { numberingSystemNames } from './data/numberingSystems.js'
import Locale from './Locale.js'
import SlotMap from '@neumatter/slot-map'
import BVON from '@neumatter/bvon'
import numbersDataRaw from './data/numbers.js'
import numberingSystemDigits from './data/numberingSystemDigits.js'

const RESOLVED_OPTIONS_KEYS = [
  'locale',
  'id'
]

export default class NumberingSystem {
  constructor (locales, options) {
    SlotMap.setInternal(this, 'internal', {})
    const slotMap = SlotMap.get(this)
    // @ts-ignore
    const requestedLocales = getCanonicalLocales(locales)
    options = Object(options || {})
    const opt = Object.create(null)
    const matcher = getOption(
      options,
      'localeMatcher',
      'string',
      ['lookup', 'best fit'],
      'best fit'
    )
    opt.localeMatcher = matcher

    const [localeData, availableLocales] = getLocaleDataLazy()

    const numberingSystem = getOption(
      options,
      'id',
      'string',
      undefined,
      undefined
    )

    if (
      numberingSystem !== undefined &&
      numberingSystemNames.indexOf(numberingSystem) < 0
    ) {
      // 8.a. If numberingSystem does not match the Unicode Locale Identifier type nonterminal,
      // throw a RangeError exception.
      throw RangeError(`Invalid numberingSystems: ${numberingSystem}`)
    }

    opt.nu = numberingSystem

    const r = resolveLocale(
      availableLocales,
      requestedLocales,
      opt,
      // [[RelevantExtensionKeys]] slot, which is a constant
      ['nu'],
      localeData,
      () => Locale.systemLocale
    )

    const dataLocaleData = localeData[r.dataLocale]
    invariant(!!dataLocaleData, `Missing locale data for ${r.dataLocale}`)
    const numSystem = r.nu || dataLocaleData.nu[0]
    slotMap.set('locale', r.locale)
    slotMap.set('id', numSystem)
    slotMap.set('digits', numberingSystemDigits[numSystem])
    slotMap.set('symbols', dataLocaleData.symbols[numSystem])
    slotMap.set('percentFormat', dataLocaleData.percent[numSystem])
    slotMap.set('decimalFormat', dataLocaleData.decimal[numSystem])
    /*
{
  nu: [ 'latn' ],
  symbols: {
    latn: {
      decimal: '.',
      group: ',',
      list: ';',
      percentSign: '%',
      plusSign: '+',
      minusSign: '-',
      approximatelySign: '~',
      exponential: 'E',
      superscriptingExponent: '×',
      perMille: '‰',
      infinity: '∞',
      nan: 'NaN',
      timeSeparator: ':',
      rangeSign: '–'
    }
  },
  percent: { latn: '#,##0%' },
  decimal: { latn: '#,##0.###' }
}
    */
  }

  formatDecimal (number) {
    let str = number.toString()
    const indexOfDecimal = str.indexOf('.')
    let decimalStr = ''
    const comma = this.group
    const decimalFormat = SlotMap.getInternal(this, 'decimalFormat')
    const digits = SlotMap.getInternal(this, 'digits')
    const decimalFormatInt = decimalFormat.slice(0, decimalFormat.indexOf('.'))
    let groupLength = 0
    let index = decimalFormatInt.length

    while (--index >= 0) {
      if (decimalFormatInt[index] === ',') {
        break
      }

      ++groupLength
    }

    if (indexOfDecimal !== -1) {
      decimalStr = str.slice(indexOfDecimal + 1)
      str = str.slice(0, indexOfDecimal)
      let dsIndex = -1
      let ds = ''

      while (++dsIndex < decimalStr.length) {
        ds += digits[Number(decimalStr[dsIndex])]
      }

      decimalStr = ds
    }

    index = str.length
    let chunks = 0
    const response = []

    while (--index >= 0) {
      ++chunks
      if (chunks === groupLength && index !== 0) {
        response[index] = comma + digits[Number(str[index])]
        chunks = 0
      } else {
        response[index] = digits[Number(str[index])]
      }
    }

    return decimalStr.length
      ? response.join('') + this.decimal + decimalStr
      : response.join('')
  }

  get id () {
    return SlotMap.getInternal(this, 'id')
  }

  get decimal () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.decimal
  }

  get group () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.group
  }

  get percentSign () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.percentSign
  }

  get plusSign () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.plusSign
  }

  get minusSign () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.minusSign
  }

  get infinity () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.infinity
  }

  get nan () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.nan
  }

  get timeSeparator () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.timeSeparator
  }

  get rangeSign () {
    const symbols = SlotMap.getInternal(this, 'symbols')
    return symbols.rangeSign
  }

  resolvedOptions () {
    const slotMap = SlotMap.get(this)
    return slotMap.createPublicRecord(RESOLVED_OPTIONS_KEYS)
  }

  toString () {
    return SlotMap.getInternal(this, 'id')
  }

  inspect () {
    return `NumberingSystem(nu-${this.toString()})`
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return `NumberingSystem(\x1b[35mnu-${this.toString()}\x1b[0m)`
  }
}

function getLocaleDataLazy () {
  let al = SlotMap.getInternal(NumberingSystem, 'availableLocales')
  let ld = SlotMap.getInternal(NumberingSystem, 'localeData')

  if (ld === undefined || al === undefined) {
    const view = decode256(numbersDataRaw)
    const data = BVON.deserialize(view)
    const localeData = {}
    const availableLocales = new Set()

    for (const locale in data) {
      if (typeof locale === 'string' && locale.length) {
        const minimizedLocale = new Locale(locale).minimize().toString()
        localeData[locale] = data[minimizedLocale]
        availableLocales.add(minimizedLocale)
        availableLocales.add(locale)
      }
    }

    SlotMap.setInternal(NumberingSystem, 'availableLocales', availableLocales)
    SlotMap.setInternal(NumberingSystem, 'localeData', localeData)
    al = availableLocales
    ld = localeData
  }

  return [ld, al]
}
